import { z } from 'zod';
import { ulidSchema } from './id';
import type { EventType } from './events';

/**
 * The card-back fields a live frame or a conflict notice can be attributed
 * to — `BE-31.1`, `BE-31.2`, `FR-COLLAB-026`, `FR-EXP-031`, `.b31-design.md`
 * §3.2. A primitive plus a wire contract, filed beside `./id` and `./rank`
 * rather than as a widening of `./card` or `./card-detail` — this module
 * answers "which field did this event change", a question neither of those
 * two files has ever needed to ask of itself, and it belongs to no route the
 * way `./mention`'s parser belongs to no route: `GET /cards/:id/field-versions`
 * (`BE-31.1`'s read half) and `apps/server/src/ws/board-socket.ts`'s card-room
 * filter (`BE-31.2`) both consume it, and neither owns it.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS AT ALL — §3.1's decision, restated at the point it is used
 * ---------------------------------------------------------------------------
 * `cards` carries a whole-row `updated_at` and no per-field version or
 * timestamp column, and nothing in the 86-table schema does. The version of a
 * card field is therefore **derived**: it is the `activity_entries.id` of the
 * most recent entry whose `type` maps to that field, and `id` is a monotonic
 * ULID under `COLLATE "C"`, so `MAX(id)` orders correctly even for two entries
 * written in the same millisecond. This file is the map that "maps to that
 * field" means — the one place the mapping is written down, so
 * `GET /cards/:id/field-versions`'s SQL and the `PATCH` routes' conflict check
 * (§3.4) can never spell it two different ways.
 *
 * ⚠ **A field nothing has ever changed has no version — one state (absent),
 * not a sentinel.** `cardFieldForEventType` returning `null` for a
 * board/list/user/workspace-scoped name and a card never having emitted a
 * mapped event are the same absence at two different layers, and both leave
 * "no version" rather than a zero or an empty-string placeholder. `§3.4`'s
 * decision table depends on being able to tell "never changed" apart from
 * "changed, and I have not seen the latest write" without a sentinel value
 * that could collide with a real ULID.
 */

/**
 * The closed set of card-back fields, ordered as the card back reads: the
 * title and description at the top, then the assignment fields (members,
 * labels, the two dates, completion, the cover), then the three collections
 * (checklists, attachments, relations), then the unbounded stream (comments),
 * and finally the two fields that are a property of the card's *row* rather
 * than of a section on its face (`placement`, `lifecycle`) — last because
 * nothing on the rendered card back has a "placement" or "lifecycle" heading
 * to sit under.
 *
 * `placement` is the list/position/board-move family: which list a card is
 * on, where in that list, and which board — `card.created` (a card's first
 * placement), `card.created_by_email` (the same first placement through
 * inbound board email), `card.added_to_list` (a boardless Inbox card's first
 * board-visible placement), `card.position_changed`, `card.moved_between_lists`
 * and `card.moved_between_boards`, plus the two other ways a card can be born
 * already placed (`card.copied_from_card`, `card.created_from_template`).
 * `lifecycle` is archived/unarchived/deleted — `FR-CARD-*`'s "is this card
 * still live" axis, orthogonal to anything the card back itself renders —
 * **and, since B37/B65, the template visibility flags**
 * (`card.template_marked` / `card.template_unmarked`, `FR-TMPL-008` /
 * `FR-TMPL-012`, and `card.template_hidden_from_list` /
 * `card.template_unhidden_from_list`, `FR-TMPL-011`), which are the same axis
 * read one notch wider: flags that take a card out of everyday flow without
 * removing it. See that pair's own note in the map below for why they fold
 * onto an existing field rather than adding a fifteenth here.
 */
export const CARD_FIELDS = [
  'title',
  'description',
  'members',
  'labels',
  'dueDate',
  'startDate',
  'completed',
  'cover',
  'checklists',
  'attachments',
  'relations',
  'comments',
  'placement',
  'lifecycle',
] as const;

export type CardField = (typeof CARD_FIELDS)[number];

/**
 * Which card field each card-scoped event type is a change to.
 *
 * ⚠ Typed so that a misspelt event name is a COMPILE error rather than a
 * runtime no-match — the same protection `ACCESS_REVALIDATION_EVENT_TYPES`
 * (`apps/server/src/ws/board-socket.ts`) buys with `readonly EventType[]`,
 * and for the same reason: a key that matches no real event name never fires
 * and nothing reddens. Here the failure would be quieter still than a
 * revalidation hole — a field whose latest change nobody ever sees a version
 * for, with `GET /cards/:id/field-versions` returning a 200 that is simply
 * missing a row, and the conflict check in §3.4 treating a genuine edit as
 * "field never changed" for ever.
 *
 * Derived from `EVENT_TYPES` in `./events` (the live vocabulary, not
 * memory) rather than from `activity.ts`'s feed grammar: a field's version is
 * a fact about the *card*, and it must track a change whether or not that
 * change is one a human reads in the feed (`surfacesInFeed` is `./activity`'s
 * discriminator, not this one) — nothing here filters on it.
 *
 * ⚠ **`card.field_overridden` deliberately maps to nothing.** It is emitted
 * *beside* `card.renamed` or `card.description_edited` in the same
 * `withTransaction` call (§3.4), never alone, so if it mapped to `title` or
 * `description` here, `MAX(activity_entries.id)` for that field could resolve
 * to the **notice** rather than to the change the notice is about — the
 * conflict record would shadow the very fact `GET /cards/:id/field-versions`
 * exists to expose. This map answers "which field is this event a change
 * to", and a record that a collision was resolved is not itself a change; it
 * is `card.field_overridden`'s own docstring's distinction (`./events`),
 * restated here as an absence rather than an entry.
 *
 * Board-scoped, list-scoped, user-scoped and workspace-scoped names are
 * absent for the same reason `cardFieldForEventType` returns `null` for
 * them: they carry `cardId: null` on the row (verified per group against
 * `./events`' own SCOPE comments — `board.*`, `list.*`, `user.*`,
 * `workspace.*`, plus the four board-level label names `label.created` /
 * `label.renamed` / `label.recoloured` / `label.deleted`, and
 * `board.permission_changed`), so there is no card whose field-version query
 * could ever surface them.
 */
export const CARD_FIELD_BY_EVENT_TYPE = {
  // ---------------------------------------------------------------------
  // title
  // ---------------------------------------------------------------------
  'card.renamed': 'title',

  // ---------------------------------------------------------------------
  // description
  // ---------------------------------------------------------------------
  'card.description_edited': 'description',

  // ---------------------------------------------------------------------
  // members — B14's join/leave and B14's assign, on one field
  // ---------------------------------------------------------------------
  'member.added': 'members',
  'member.removed': 'members',
  'member.self_joined': 'members',
  'member.self_left': 'members',

  // ---------------------------------------------------------------------
  // labels — the two CARD-scoped names of B15's six; the other four
  // (label.created/renamed/recoloured/deleted) are board-scoped and absent
  // from this map on that ground, not by oversight.
  // ---------------------------------------------------------------------
  'label.added': 'labels',
  'label.removed': 'labels',

  // ---------------------------------------------------------------------
  // dueDate
  // ---------------------------------------------------------------------
  'due.date_set': 'dueDate',
  'due.date_changed': 'dueDate',
  'due.date_removed': 'dueDate',

  // ---------------------------------------------------------------------
  // startDate
  // ---------------------------------------------------------------------
  'start.date_set': 'startDate',
  'start.date_removed': 'startDate',

  // ---------------------------------------------------------------------
  // completed — a toggle independent of dueDate (FR-CARD-004's own text:
  // "Completion is independent of due dates"), so its two names are their
  // own field rather than folded into dueDate.
  // ---------------------------------------------------------------------
  'due.date_completed': 'completed',
  'due.date_uncompleted': 'completed',

  // ---------------------------------------------------------------------
  // cover
  // ---------------------------------------------------------------------
  'cover.set': 'cover',
  'cover.removed': 'cover',

  // ---------------------------------------------------------------------
  // checklists — the whole subsystem (the checklist row, its position, and
  // every item gesture) is one field on the card back, on `./checklist`'s
  // own footing: an item has no meaning and no route independent of its
  // checklist, so a version at item granularity would be a field this
  // package invents and no route reads.
  // ---------------------------------------------------------------------
  'checklist.added': 'checklists',
  'checklist.renamed': 'checklists',
  'checklist.position_changed': 'checklists',
  'checklist.removed': 'checklists',
  'checklist.item_added': 'checklists',
  'checklist.item_edited': 'checklists',
  'checklist.item_completed': 'checklists',
  'checklist.item_uncompleted': 'checklists',
  'checklist.item_position_changed': 'checklists',
  'checklist.item_moved': 'checklists',
  'checklist.item_removed': 'checklists',

  // ---------------------------------------------------------------------
  // attachments
  // ---------------------------------------------------------------------
  'attachment.added': 'attachments',
  'attachment.renamed': 'attachments',
  'attachment.removed': 'attachments',

  // ---------------------------------------------------------------------
  // relations — B22's card-to-card links, `FR-TMPL-028`
  // ---------------------------------------------------------------------
  'card.relation_added': 'relations',
  'card.relation_removed': 'relations',

  // ---------------------------------------------------------------------
  // comments
  // ---------------------------------------------------------------------
  'comment.added': 'comments',
  'comment.edited': 'comments',
  'comment.deleted': 'comments',

  // ---------------------------------------------------------------------
  // placement — the list/position/board-move family. `card.created` maps
  // here rather than to nothing: a card is placed onto a list at birth, and
  // that IS the field's first version, on §3.1's own "never changed has no
  // version" rule read the other way round — a brand-new card has a
  // `placement` version and (correctly) no `title` version, because
  // creation sets the title without EDITING it.
  //
  // ⚠ B43's `card.added_to_list` joins them for the same reason from the
  // opposite direction: the card already exists, but it had no board/list
  // placement until filing. No `card.created` accompanies the filing event, so
  // this row is the placement field's first board-visible version.
  //
  // ⚠ B49's `card.created_by_email` joins them from the ordinary creation
  // side: the card is born placed on a board/list, only the source of that
  // creation differs. That source matters to the activity sentence, not to the
  // field-version axis.
  //
  // ⚠ B35's `card.copied_from_card` joins them for exactly that reason and
  // NOT as a second exception beside `card.field_overridden`. It is a card's
  // own creation entry — the copy is born placed on a list, and no
  // `card.created` accompanies it — so `card.created`'s argument above
  // applies to it whole. `card.field_overridden` is excluded from this map
  // because it is a NOTICE about a change rather than a change (see
  // `cardFieldForEventType`); a copy is not a notice about anything.
  // ---------------------------------------------------------------------
  //
  // ⚠ B37's `card.created_from_template` joins them on `card.copied_from_
  // card`'s own footing, one sentence over: a card created from a template
  // is born placed on a list, no `card.created` accompanies it, and
  // `card.created`'s argument therefore applies to it whole. It is a
  // separate name from `card.copied_from_card` — see `./events`, which
  // argues that at length — but it is not a separate KIND of thing here:
  // both are a card's own first placement, and mapping one to `placement`
  // and the other to nothing would make two routes that both create a card
  // disagree about whether the card has a placement version.
  'card.created': 'placement',
  'card.created_by_email': 'placement',
  'card.added_to_list': 'placement',
  'card.copied_from_card': 'placement',
  'card.merged': 'placement',
  'card.created_from_template': 'placement',
  'card.link_card_created': 'placement',
  'card.position_changed': 'placement',
  'card.moved_between_lists': 'placement',
  'card.moved_between_boards': 'placement',

  // ---------------------------------------------------------------------
  // lifecycle — archived/unarchived/deleted, `FR-CARD-*`'s "is this card
  // still live" axis. Not itself a section the card back renders, unlike
  // every field above it.
  // ---------------------------------------------------------------------
  'card.archived': 'lifecycle',
  'card.unarchived': 'lifecycle',
  'card.deleted': 'lifecycle',
  // ⚠ B37's template flag pair (`FR-TMPL-008`, `FR-TMPL-012`) lands HERE,
  // and this is the one placement in this map that is a judgement rather
  // than a reading. `cards.is_template` is not a section the card back
  // renders and it is not where the card is, which leaves `lifecycle` — the
  // field this file's own header describes as "a property of the card's ROW
  // rather than of a section on its face" — as the only member of
  // `CARD_FIELDS` it can honestly fold onto.
  //
  // The axis is the same one `card.archived` is on, read one notch wider
  // than "is this card still live": both flags take a card that is present
  // in the data OUT OF EVERYDAY FLOW without deleting it, which is why
  // `FR-TMPL-011` ("keep template cards out of everyday board flow", hidden
  // from its list and excluded from its list's card count) reads as an
  // archive by another name, and why `FR-TMPL-008` hides the card's dates
  // and completion state exactly as an archived card stops being work in
  // progress. A client watching this field's version to notice an archival
  // will therefore also see a template toggle — stated rather than
  // discovered, because it is the cost of this decision.
  //
  // ⚠ The alternative was a FIFTEENTH `CardField`, and it is refused on the
  // wire rather than on taste: `CARD_FIELDS` is a closed, ORDERED set that
  // `cardFieldVersionsSchema` serialises and `GET /cards/:id/field-versions`
  // emits in that order, so a fifteenth member widens a shipped response for
  // a facet no route reads a version of. The other alternative — mapping
  // both to nothing, `card.field_overridden`'s exception — fails on that
  // exception's OWN reason: it is excluded because a notice about a change
  // is not a change, and marking a card as a template is a change to
  // `cards`, written by a route, in a transaction, like every other entry in
  // this map.
  'card.template_marked': 'lifecycle',
  'card.template_unmarked': 'lifecycle',
  'card.template_hidden_from_list': 'lifecycle',
  'card.template_unhidden_from_list': 'lifecycle',
  'card.link_card_converted': 'lifecycle',
} as const satisfies Partial<Record<EventType, CardField>>;

/**
 * The map, typed for lookup by an arbitrary `EventType` — `generate.ts`'s
 * `RULES_FOR` idiom, restated here rather than reinvented. `CARD_FIELD_BY_
 * EVENT_TYPE`'s own inferred type has only its forty-seven literal keys,
 * which is exactly what makes the `satisfies` above catch a typo; a second,
 * widened binding is what lets `cardFieldForEventType` index it with a type
 * that ranges over the whole vocabulary without TypeScript narrowing the
 * argument down to those forty-seven keys first.
 */
const FIELD_FOR: Partial<Record<EventType, CardField>> = CARD_FIELD_BY_EVENT_TYPE;

/**
 * Which card field `type` is a change to, or `null` for a board-, list-,
 * user- or workspace-scoped name, or for `card.field_overridden` itself.
 */
export function cardFieldForEventType(type: EventType): CardField | null {
  return FIELD_FOR[type] ?? null;
}

/**
 * The card fields a conditional write and an override notice cover.
 *
 * ⚠ EXACTLY TWO, AND THE NARROWING IS A DECISION WITH AN AC BEHIND IT.
 * `FR-COLLAB-026` AC2 is the DESCRIPTION; AC3 and AC4 are "renders live", not
 * conflict notices. Members, labels, dates and checklist ticks are discrete
 * toggles rather than drafts — there is no in-progress text for a later write
 * to clobber, so last-write-wins there is invisible and a notice would be
 * noise. Title joins description because `FR-CARD-002` makes it the other
 * free-text field a viewer types into.
 *
 * `card-detail.ts:1170-1172`, `PATCH /cards/:id/description`'s own route
 * description, verbatim: "Phase 1 is last-write-wins within the field, and
 * that is a known, accepted gap rather than an oversight: B31 owns collision
 * handling, and this event's payload (who, when, and how the length moved)
 * is the only record a lost edit leaves." B14 named this block, named the
 * gap, and identified precisely what was missing. This constant, and
 * `cardFieldOverridePayloadSchema` below, are that deferral being honoured.
 */
export const CONCURRENCY_CONTROLLED_CARD_FIELDS = ['title', 'description'] as const;

export type ConcurrencyControlledCardField =
  (typeof CONCURRENCY_CONTROLLED_CARD_FIELDS)[number];

/**
 * One card field's current version — the `activity_entries.id` of the latest
 * entry whose type `cardFieldForEventType` maps to `field`, and who wrote it.
 */
export const cardFieldVersionSchema = z.object({
  field: z.enum(CARD_FIELDS),
  /** The `activity_entries.id` of the latest change — a monotonic ULID. */
  version: ulidSchema,
  updatedAt: z.string(),
  /** `null` for an automation actor, on every other event schema's rule. */
  actorUserId: ulidSchema.nullable(),
});

export type CardFieldVersion = z.infer<typeof cardFieldVersionSchema>;

/**
 * `GET /cards/:id/field-versions`'s response — `BE-31.1`'s read half.
 *
 * ⚠ An ARRAY, and only the fields that have ever changed.
 *
 * A `z.record` over `CARD_FIELDS` would make "never changed" expressible two
 * ways — an absent key and a null value — and a client would have to handle
 * both. Absent is the one spelling: it is what §3.1 already means by "one
 * state, not a sentinel", carried from the derivation into the wire shape
 * rather than re-decided here. The route (`apps/server/src/routes/
 * card-field-versions.ts`) is responsible for emitting `fields` in
 * `CARD_FIELDS` order so the response is stable and diffable; this schema
 * does not enforce that ordering itself; an array in any order still
 * satisfies it.
 */
export const cardFieldVersionsSchema = z.object({
  cardId: ulidSchema,
  fields: z.array(cardFieldVersionSchema),
});

export type CardFieldVersions = z.infer<typeof cardFieldVersionsSchema>;

/**
 * The `payload` of a `card.field_overridden` entry — the "override notice
 * payload" `BE-31.1`'s item line names.
 *
 * ⚠⚠ `overriddenValue` CARRIES THE TEXT, AND THAT IS A DELIBERATE DEVIATION
 * FROM B14's RULE. `PATCH /cards/:id/description`'s own route description
 * says "The activity entry records the previous and new LENGTHS, never the
 * text", and `card.description_edited` KEEPS that behaviour untouched. The
 * exception is this one name, and its authority is `FR-EXP-031` AC4 — "the
 * overridden version is retrievable from the card's activity" — which cannot
 * be satisfied by a length. The narrowing that makes the exception safe: this
 * entry exists only on a genuine conflict, so the text is stored on the rare
 * path and never on the routine edit. `null` means the field was empty.
 *
 * ⚠ **No `.max()` here, and that is not an oversight — it is `./card`'s own
 * rule (never share one bound across two columns) applied to a field this
 * schema does not own.** `title` and `description` carry two different
 * limits (`CARD_TITLE_MAX_LENGTH` in `./board`; `PATCH /cards/:id/description`
 * bounds its own body at 16,384 characters), each already enforced at the
 * patch route that produced the text this payload is quoting back. A single
 * `.max()` on `overriddenValue` would be correct for one of the two fields
 * and a lie about the other's actual limit; the schema invents no third bound
 * of its own, on §3.2's "restate no constant" rule read the other way — there
 * is no *one* constant here to import, because `field` decides which one
 * already applied upstream.
 *
 * ⚠ B21's payload rule holds: no key here names a row the audience of this
 * row's own `board_id` could not already see — the overridden value is the
 * card's own former text, readable by every member of that board.
 */
export const cardFieldOverridePayloadSchema = z.object({
  field: z.enum(CONCURRENCY_CONTROLLED_CARD_FIELDS),
  /** What the writer believed was current, from the `if-field-version` header. */
  expectedVersion: ulidSchema,
  /** What actually was current at the moment the write landed. */
  overriddenVersion: ulidSchema,
  overriddenActorUserId: ulidSchema.nullable(),
  overriddenAt: z.string(),
  overriddenValue: z.string().nullable(),
  /** The payload convention every card event follows (`./events`). */
  workspaceId: ulidSchema,
});

export type CardFieldOverridePayload = z.infer<typeof cardFieldOverridePayloadSchema>;
